import { useCallback, useMemo } from "react";
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Select,
  Textarea,
  Alert,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { billingService } from "../../../services/billing.service";
import { cptCodesService } from "../../../services/admin/cptcodes.service";
import { useHasPermission } from "../../../hooks/useHasPermission";

interface CptCorrectionModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  caseDiagnosisId: string;
  specimenName: string;
  currentCptCode: { id: string; code: string; description: string } | null;
  onSuccess: () => void;
}

export function CptCorrectionModal({
  opened,
  onClose,
  caseId,
  caseNumber,
  caseDiagnosisId,
  specimenName,
  currentCptCode,
  onSuccess,
}: CptCorrectionModalProps) {
  const queryClient = useQueryClient();
  const canDirectApply = useHasPermission("billing.manage");

  const form = useForm({
    initialValues: {
      newCptCodeId: "",
      reason: "",
    },
    validate: {
      newCptCodeId: (value: string) =>
        !value ? "New CPT code is required" : null,
      reason: (value: string) =>
        !value ? "Reason for correction is required" : null,
    },
    validateInputOnBlur: true,
  });

  // Load available CPT codes for the Select dropdown
  const { data: cptCodesList, isLoading } = useQuery({
    queryKey: ["cpt-codes-all-select"],
    queryFn: () => cptCodesService.getAll(),
    enabled: opened,
  });

  const selectData = useMemo(() => {
    if (!cptCodesList) return [];
    return cptCodesList
      .filter((c) => c.isActive && c.id !== currentCptCode?.id)
      .map((c) => ({
        value: c.id,
        label: `${c.code} - ${c.description}`,
      }));
  }, [cptCodesList, currentCptCode?.id]);

  const mutation = useMutation({
    mutationFn: (variables: {
      caseDiagnosisId: string;
      newCptCodeId: string;
      reason: string;
      action: "apply" | "request";
    }) => billingService.updateCaseCpt(caseId, variables),
    onSuccess: (data) => {
      notifications.show({
        title: "Success",
        message: data.message || "CPT code updated successfully.",
        color: "green",
      });
      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["correction-requests", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["all-correction-requests"] });
      onSuccess();
      form.reset();
      onClose();
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.message || "Failed to update CPT code.",
        color: "red",
      });
    },
  });

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleDirectApply = useCallback(() => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    mutation.mutate({
      caseDiagnosisId,
      newCptCodeId: form.values.newCptCodeId,
      reason: form.values.reason,
      action: "apply",
    });
  }, [form, caseDiagnosisId, mutation]);

  const handleRequestCorrection = useCallback(() => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    mutation.mutate({
      caseDiagnosisId,
      newCptCodeId: form.values.newCptCodeId,
      reason: form.values.reason,
      action: "request",
    });
  }, [form, caseDiagnosisId, mutation]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={700} size="lg">
          CPT Code Correction — Case {caseNumber}
        </Text>
      }
      size="md"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          variant="light"
        >
          <Text size="xs" fw={500}>
            Specimen: {specimenName}
          </Text>
          <Text size="xs" fw={500}>
            Current CPT:{" "}
            {currentCptCode
              ? `${currentCptCode.code} (${currentCptCode.description})`
              : "None"}
          </Text>
        </Alert>

        <Select
          label="New CPT Code"
          placeholder={isLoading ? "Loading CPT codes..." : "Select CPT code"}
          data={selectData}
          searchable
          disabled={isLoading}
          error={form.errors.newCptCodeId}
          {...form.getInputProps("newCptCodeId")}
        />

        <Textarea
          label="Reason for Correction"
          placeholder="Please explain why the CPT code is being updated..."
          minRows={3}
          error={form.errors.reason}
          {...form.getInputProps("reason")}
        />

        <Group justify="flex-end" gap="xs" mt="lg">
          <Button
            variant="default"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>

          <Button
            color="orange"
            onClick={handleRequestCorrection}
            loading={
              mutation.isPending && mutation.variables?.action === "request"
            }
            disabled={
              mutation.isPending && mutation.variables?.action !== "request"
            }
          >
            Request Pathologist Review
          </Button>

          {canDirectApply && (
            <Button
              color="blue"
              onClick={handleDirectApply}
              loading={
                mutation.isPending && mutation.variables?.action === "apply"
              }
              disabled={
                mutation.isPending && mutation.variables?.action !== "apply"
              }
            >
              Direct Apply
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
